import { cn } from "@/core/utils/custom-styles";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { type ReactNode, useMemo } from "react";
import { DataTablePaginationProps } from "./types";

const MAX_VISIBLE_BUTTONS = 5;

const paginationAlignClasses = {
  left: "justify-start",
  center: "justify-center",
  right: "justify-end",
} as const;

function PaginationSegment({
  children,
  className,
  isActive = false,
  disabled = false,
  onClick,
  ariaLabel,
}: {
  children: ReactNode;
  className?: string;
  isActive?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex h-10 min-w-10 items-center justify-center bg-white px-3 text-sm font-normal text-darkgray-900 transition-colors",
        "hover:bg-[#F5F5F5] disabled:cursor-not-allowed disabled:opacity-40",
        isActive && "bg-[#EBEBEB] hover:bg-[#EBEBEB]",
        className,
      )}
    >
      {children}
    </button>
  );
}

export function DataTablePagination<TData>({
  table,
  align = "right",
}: DataTablePaginationProps<TData>) {
  const pagination = useMemo(() => {
    const page = table.getState().pagination.pageIndex + 1;
    const pages = table.getPageCount();
    const total = table.getTotalSize();
    const maxVisibleButtons = MAX_VISIBLE_BUTTONS;
    return { page, pages, total, maxVisibleButtons };
  }, [
    table.getState().pagination.pageIndex,
    table.getPageCount(),
    table.getTotalSize(),
  ]);

  const renderPaginationButton = (item: number) => {
    const isActive = pagination.page === item;

    return (
      <PaginationSegment
        key={item}
        isActive={isActive}
        ariaLabel={`Página ${item}`}
        onClick={() => table.setPageIndex(item - 1)}
      >
        {item}
      </PaginationSegment>
    );
  };

  const renderPaginationButtons = () => {
    const renderedButtons: ReactNode[] = [];

    if (pagination.pages <= pagination.maxVisibleButtons) {
      return Array.from({ length: pagination.pages }, (_, i) =>
        renderPaginationButton(i + 1),
      );
    }

    let start = 1;
    let end = pagination.maxVisibleButtons - 1;

    if (pagination.page >= pagination.maxVisibleButtons) {
      start = pagination.page - 3;
      end = pagination.page + 1;
    }

    if (start > 2) {
      renderedButtons.push(renderPaginationButton(1));
      if (start > 3) {
        renderedButtons.push(
          <PaginationSegment key="ellipsis-button-start" disabled>
            ...
          </PaginationSegment>,
        );
      }
    }

    if (end > pagination.pages - 1) {
      start -= end - (pagination.pages - 1);
      end = pagination.pages - 1;
    }

    for (let i = start; i <= end; i++) {
      renderedButtons.push(renderPaginationButton(i));
    }

    if (end < pagination.pages - 1) {
      renderedButtons.push(
        <PaginationSegment key="ellipsis-button-end" disabled>
          ...
        </PaginationSegment>,
      );
    }

    renderedButtons.push(renderPaginationButton(pagination.pages));

    return renderedButtons;
  };

  return (
    <div
      className={cn("flex flex-1 items-center", paginationAlignClasses[align])}
    >
      <nav
        className="inline-flex overflow-hidden rounded-lg border border-[#EBEBEB] bg-white divide-x divide-[#EBEBEB]"
        aria-label="Pagination"
      >
        <PaginationSegment
          ariaLabel="Página anterior"
          disabled={!table.getCanPreviousPage()}
          onClick={() => table.previousPage()}
        >
          <ChevronLeftIcon className="size-4" aria-hidden="true" />
        </PaginationSegment>

        {renderPaginationButtons()}

        <PaginationSegment
          ariaLabel="Próxima página"
          disabled={!table.getCanNextPage()}
          onClick={() => table.nextPage()}
        >
          <ChevronRightIcon className="size-4" aria-hidden="true" />
        </PaginationSegment>
      </nav>
    </div>
  );
}
